import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import type { ApiFailure, ApiResult } from '@/api/client'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { CAP_MAX, CAP_MIN, CHANNELS, MAX_STEPS } from './channels'
import type { Step } from './channels'

export interface FollowUpsValue {
  steps: Step[]
  /** Follow-ups a day, as typed — see `cap` below for why it is a string. */
  cap: string
}

export interface FollowUpsSubmission {
  steps: Step[]
  cap: number
}

/**
 * The follow-up ladder and the daily cap, as one form used by the Settings
 * panel and the wizard step.
 *
 * Settings consolidation, batch 2 (2026-09-15): moved from `pages/outreach/Compose`,
 * where the ladder table and the cap field sat open under the brief and were
 * saved by the same *Save* that saved the brief. The brief stays there — it is
 * what that page is for. This form owns the two settings and their failure;
 * the caller decides what a successful submit does next.
 *
 * **`wait_days` is labelled *after the previous step*, not as absolute offsets**,
 * because that is what it means: a rung that could not go out on its day — a
 * shut send window, a spent cap — moves the ones after it rather than letting
 * them bunch up against it. `app/outreach/followups.py` argues it at length.
 *
 * The cap is held as the raw string, not a number (WEB-15): `Number('')` is 0,
 * so clearing the field would put a value the backend refuses (minimum 1) into
 * state, and `min`/`max` constrain the spinner, not what may be typed.
 */
export function FollowUpsForm({
  value,
  onSubmit,
  submitLabel,
  disabled = false,
}: {
  value: FollowUpsValue
  onSubmit: (value: FollowUpsSubmission) => Promise<ApiResult<unknown>>
  submitLabel: string
  disabled?: boolean
}): JSX.Element {
  const [steps, setSteps] = useState<Step[]>(value.steps)
  const [cap, setCap] = useState(value.cap)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)

  const capNumber = Number(cap)
  const capValid =
    cap.trim() !== '' && Number.isInteger(capNumber) && capNumber >= CAP_MIN && capNumber <= CAP_MAX
  const capError = capValid
    ? undefined
    : `A whole number between ${String(CAP_MIN)} and ${String(CAP_MAX)}.`

  const update = (index: number, patch: Partial<Step>): void => {
    setFailure(null)
    setSteps((current) => current.map((step, at) => (at === index ? { ...step, ...patch } : step)))
  }

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!capValid) return
    setBusy(true)
    const result = await onSubmit({ steps, cap: capNumber })
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form className="ar-stack" aria-label="Follow-ups" onSubmit={(event) => void submit(event)}>
      <div className="ar-row">
        <strong>Follow-up ladder</strong>
        <span className="ar-card__spacer" />
        <Button
          size="sm"
          disabled={disabled || steps.length >= MAX_STEPS}
          onClick={() => {
            setFailure(null)
            setSteps((current) => {
              const next = (current[current.length - 1]?.step_no ?? 0) + 1
              return [
                ...current,
                {
                  step_no: next,
                  label: `Step ${String(next)}`,
                  wait_days: 3,
                  channel: 'email',
                  angle: '',
                  enabled: true,
                },
              ]
            })
          }}
        >
          Add a step
        </Button>
      </div>
      {/* `app-compose__ladder`: the table fits its card rather than scrolling
          inside the wrap (parity P-COMP-04). The class kept its name when the
          table moved here. */}
      <div className="ar-table-wrap app-compose__ladder" tabIndex={0}>
        <table className="ar-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Wait after the previous step</th>
              <th>Channel</th>
              <th>Angle</th>
              <th>On</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step, index) => (
              <tr key={step.step_no}>
                <td>
                  <strong>{step.step_no}</strong>{' '}
                  <input
                    className="ar-input app-compose__label"
                    type="text"
                    aria-label={`Name for step ${String(step.step_no)}`}
                    disabled={disabled}
                    value={step.label}
                    onChange={(event) => {
                      update(index, { label: event.target.value })
                    }}
                  />
                </td>
                <td>
                  {step.step_no === 1 ? (
                    <span className="ar-muted">Immediately</span>
                  ) : (
                    <input
                      className="ar-input app-compose__days"
                      type="number"
                      min={0}
                      max={90}
                      aria-label={`Days to wait before step ${String(step.step_no)}`}
                      disabled={disabled}
                      value={step.wait_days}
                      onChange={(event) => {
                        update(index, { wait_days: Number(event.target.value) })
                      }}
                    />
                  )}
                </td>
                <td>
                  <select
                    className="ar-select app-compose__channel"
                    aria-label={`Channel for step ${String(step.step_no)}`}
                    disabled={disabled}
                    value={step.channel}
                    onChange={(event) => {
                      update(index, { channel: event.target.value })
                    }}
                  >
                    {CHANNELS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="ar-input"
                    type="text"
                    aria-label={`Angle for step ${String(step.step_no)}`}
                    disabled={disabled}
                    value={step.angle}
                    onChange={(event) => {
                      update(index, { angle: event.target.value })
                    }}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Step ${String(step.step_no)} is on`}
                    disabled={disabled}
                    checked={step.enabled}
                    onChange={(event) => {
                      update(index, { enabled: event.target.checked })
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
        Any reply, on any channel, ends the ladder for that person. So does an unsubscribe, a STOP,
        or a bounce — those also add them to the suppression list.
      </p>

      <Field
        label="Follow-ups a day"
        help="Counted across every channel and every step, in your own day (IST). The send window decides the hours; this decides the volume."
        error={capError}
      >
        {(aria) => (
          <input
            {...aria}
            className="ar-input"
            type="number"
            min={CAP_MIN}
            max={CAP_MAX}
            disabled={disabled}
            value={cap}
            onChange={(event) => {
              setFailure(null)
              setCap(event.target.value)
            }}
          />
        )}
      </Field>

      <InlineError failure={failure} />

      <div className="ar-row">
        <span className="ar-card__spacer" />
        <Button
          type="submit"
          tone="primary"
          disabled={disabled || !capValid}
          busy={busy}
          busyLabel="Saving"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
